import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, AlertCircle, Info, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Alert banner. Surface container with a single 1px outline
 * (NO colored border, NO colored left accent). Emphasis comes from a tinted
 * lead-icon chip whose tint + icon color encode the variant. Container keeps
 * bg-sober-surface; only the chip carries the variant tint. Maps to the
 * app.css .alert/.alert--* spec.
 */
const alertIconVariants = cva(
  'grid h-10 w-10 shrink-0 place-items-center rounded-so-md [&_svg]:size-5',
  {
    variants: {
      variant: {
        danger: 'bg-sober-red-bg text-sober-red',
        warning: 'bg-sober-yellow-bg text-sober-yellow-fg',
        info: 'bg-sober-primary-bg text-sober-primary',
        success: 'bg-sober-green-bg text-sober-green-fg',
      },
    },
    defaultVariants: { variant: 'info' },
  }
);

const DEFAULT_ICON: Record<NonNullable<AlertVariant>, React.ReactNode> = {
  danger: <AlertCircle />,
  warning: <AlertTriangle />,
  info: <Info />,
  success: <CheckCircle2 />,
};

type AlertVariant = NonNullable<VariantProps<typeof alertIconVariants>['variant']>;

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof alertIconVariants> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    { className, variant = 'info', title, description, icon, action, children, ...props },
    ref
  ) => {
    const resolvedVariant: AlertVariant = variant ?? 'info';
    const leadIcon = icon ?? DEFAULT_ICON[resolvedVariant];
    const body = description ?? children;
    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          'flex flex-wrap items-start gap-4 rounded-so-lg border border-sober-outline bg-sober-surface px-5 p-4 shadow-so-card sm:flex-nowrap',
          className
        )}
        {...props}
      >
        <span aria-hidden="true" className={cn(alertIconVariants({ variant: resolvedVariant }))}>
          {leadIcon}
        </span>
        <div className="min-w-0 flex-1">
          {title ? (
            <div className="text-so-14 font-bold text-sober-text">{title}</div>
          ) : null}
          {body ? (
            <div className={cn('text-so-14 text-sober-secondary', title ? 'mt-0.5' : null)}>
              {body}
            </div>
          ) : null}
        </div>
        {action ? <div className="w-full sm:w-auto sm:shrink-0">{action}</div> : null}
      </div>
    );
  }
);
Alert.displayName = 'Alert';

export { alertIconVariants };
